import { useState, useEffect } from "react";
import api from "@/lib/api";

/**
 * Real "is a dealer actually broadcasting" status for the live-dealer surfaces.
 * - bjLive:       any standard Live Blackjack table has a dealer streaming (live input connected)
 * - baccaratLive: a dealer is actually broadcasting to the Live Baccarat feed (server `live`)
 * - tableLive:    per blackjack table_id -> is that table's feed broadcasting now
 * - anyLive:      any of the above
 * Polls lightly so entry-point "Live" badges never claim live while dealers are offline.
 */
export function useLiveDealerStatus(pollMs = 15000) {
  const [status, setStatus] = useState({ bjLive: false, baccaratLive: false, anyLive: false, loaded: false, tableLive: {} });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const [bj, bac] = await Promise.allSettled([
        api.get("/casino/bj/tables"),
        api.get("/live/current"),
      ]);
      // Per-table broadcast truth (table_id -> is a dealer actually streaming now). Every
      // internal live-dealer tile badges LIVE off its OWN table's signal, so e.g. Breakout
      // never claims LIVE just because Main has a dealer.
      const tableLive = {};
      let anyBj = false;
      if (bj.status === "fulfilled" && Array.isArray(bj.value?.data)) {
        bj.value.data.forEach((t) => {
          const isLive = !!t?.live;
          if (t?.table_id) tableLive[t.table_id] = isLive;
          if (isLive) anyBj = true;
        });
      }
      // The shared "Live Blackjack" host tile covers the standard tables; the dedicated
      // Breakout tile carries its own badge, so exclude it here to avoid cross-claiming.
      const bjLive = Object.keys(tableLive).some((id) => id !== "breakout" && tableLive[id]);
      // Feed-driven: Baccarat is live only when a dealer is actually broadcasting to its
      // Cloudflare input (server-computed `live`), not merely because a round row exists.
      const baccaratLive = bac.status === "fulfilled" && !!bac.value?.data?.live;
      if (alive) setStatus({ bjLive, baccaratLive, anyLive: anyBj || baccaratLive, loaded: true, tableLive });
    };
    load();
    const id = setInterval(load, pollMs);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [pollMs]);

  return status;
}
